import { Fragment, useLayoutEffect, useMemo, useRef } from 'react';
import { isPlainObject, noop } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton, Tooltip } from '@mui/material';
import { SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import {
  useAppStore,
  useManageDashboardActions,
  usePerformanceReportBuilderPageStore,
} from '@diana/core';
import { StyledIconButton } from '../PerformanceReportBuilderPage.styles';
import { usePerformanceReportBuilderPageContext } from '../PerformanceReportBuilderPageContext';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';

export const SettingsButton = () => {
  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const canReloadRef = useRef(false);

  const queryClient = useQueryClient();

  const isToolDrawerOpen = useAppStore((s) => s.isToolDrawerOpen);

  const layout = usePerformanceReportBuilderPageStore((s) => s.layout);
  const getLayout = usePerformanceReportBuilderPageStore((s) => s.getLayout);

  const { reloadLayout, isLoading, error } = usePerformanceReportBuilderPageContext();
  const { actions = [] } = useManageDashboardActions({ id: layout?.id ?? '', setProcessing: noop });
  const manageAction = useMemo(() => {
    return (
      actions.find((action) => action.testId === 'manage-performance-dashboard-action') ?? null
    );
  }, [actions]);

  useLayoutEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      const layout = getLayout();
      if (layout && event.type === 'updated' && event.action.type === 'success') {
        const data = isPlainObject(event.action.data) ? event.action.data : {};
        if ('id' in data && data.id === layout.id && canReloadRef.current) reloadLayout();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, getLayout]);

  useLayoutEffect(() => {
    if (!isToolDrawerOpen) {
      const timeoutId = setTimeout(() => {
        canReloadRef.current = false;
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [isToolDrawerOpen]);

  if (isLoading) return <Skeleton variant="circular" width={32} height={32} />;

  if (!error)
    return (
      <Tooltip title={t('tooltips.settings')}>
        <StyledIconButton
          data-testid="SettingsButton"
          size="small"
          disabled={isLoading}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (manageAction && manageAction.onClick) {
              manageAction.onClick?.({ close: noop });
              canReloadRef.current = true;
            }
          }}
        >
          <SettingsIcon fontSize="small" />
        </StyledIconButton>
      </Tooltip>
    );

  return <Fragment />;
};
